import React, { useEffect, useMemo, useState } from "react";
import { OrbitProgress } from "react-loading-indicators";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
// Imports the icons used by the shop table actions.
import {
  ArrowUpDown,
  Check,
  Eye,
  Store,
  X,
  Pencil,
  Logs,
  EyeOff,
  Info,
} from "lucide-react";

import TableFilterPopover from "./TableFilterPopover.jsx";
import { useApiPopup } from "../ApiPopup/ApiPopupProvider.jsx";

import { useAllShopsData } from "../../../services/API/ShopAPI/GetAllShopDetails.jsx";
import { useProjectIdData } from "../../../services/API/ShopAPI/UpdateProjectIdAPI.jsx";

// Imports the mutation used to persist the hidden shop status.
import { useShopHiddenStatusData } from "../../../services/API/ShopAPI/UpdateShopHiddenStatusAPI.jsx";

const ShopListTable = ({ onOpenShopLogs }) => {
  const popup = useApiPopup();

  const {
    data: allShopsData = [],
    isPending: isAllShopsDataPending,
    error: allShopsDataError,
  } = useAllShopsData();

  const updateProjectIdMutation = useProjectIdData();
  // Handles requests to hide or restore shops.
  const updateShopHiddenStatusMutation = useShopHiddenStatusData();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedShop, setSelectedShop] = useState("");
  const [sorting, setSorting] = useState([]);

  const [editingShopId, setEditingShopId] = useState(null);
  const [projectDraft, setProjectDraft] = useState("");

  // Stores the IDs of shops that are currently hidden.
  const [hiddenShopIds, setHiddenShopIds] = useState([]);

  // Controls whether the table displays hidden shops.
  const [showHiddenShops, setShowHiddenShops] = useState(false);

  // Synchronizes hidden shop IDs with the status stored in the backend.
  useEffect(() => {
    const hiddenIds = allShopsData
      .filter((shop) => shop.is_hidden === true)
      .map((shop) => shop.id);

    setHiddenShopIds(hiddenIds);
  }, [allShopsData]);

  // Controls the simple shop information popup.
  const [isShopInfoOpen, setIsShopInfoOpen] = useState(false);
  const [selectedShopInfo, setSelectedShopInfo] = useState(null);

  // Opens the simple shop information popup.
  const openShopInfo = (shop) => {
    setSelectedShopInfo(shop);
    setIsShopInfoOpen(true);
  };

  // Closes the simple shop information popup.
  const closeShopInfo = () => {
    setIsShopInfoOpen(false);
    setSelectedShopInfo(null);
  };

  // Closes the popup when the Escape key is pressed.
  useEffect(() => {
    if (!isShopInfoOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeShopInfo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isShopInfoOpen]);

  // Prevents the page behind the popup from scrolling.
  useEffect(() => {
    if (!isShopInfoOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isShopInfoOpen]);

  const startEditProjectId = (shop) => {
    setEditingShopId(shop.id);
    setProjectDraft(shop.projectNumber ?? "");
  };

  const cancelEditProjectId = () => {
    setEditingShopId(null);
    setProjectDraft("");
  };

  // Hides a shop and persists the hidden status in the backend.
  const hideShop = (shop) => {
    updateShopHiddenStatusMutation.mutate(
      {
        shopId: shop.id,
        isHidden: true,
      },
      {
        onSuccess: () => {
          setHiddenShopIds((previous) => {
            if (previous.includes(shop.id)) {
              return previous;
            }

            return [...previous, shop.id];
          });

          popup.success("Shop hidden successfully.");
        },

        onError: (error) => {
          popup.error(error?.message || "Failed to hide shop.");
        },
      },
    );
  };

  // Restores a hidden shop and persists the status in the backend.
  const restoreShop = (shop) => {
    updateShopHiddenStatusMutation.mutate(
      {
        shopId: shop.id,
        isHidden: false,
      },
      {
        onSuccess: () => {
          setHiddenShopIds((previous) =>
            previous.filter((shopId) => shopId !== shop.id),
          );

          popup.success("Shop restored successfully.");
        },

        onError: (error) => {
          popup.error(error?.message || "Failed to restore shop.");
        },
      },
    );
  };

  const saveEditProject = (shop) => {
    const newProjectId = String(projectDraft ?? "").trim();

    updateProjectIdMutation.mutate(
      {
        shopId: shop.id,
        projectId: newProjectId,
      },
      {
        onSuccess: () => {
          popup.success("Project ID updated successfully.");
          setEditingShopId(null);
          setProjectDraft("");
        },

        onError: (error) => {
          popup.error(error?.message || "Failed to update Project ID.");
        },
      },
    );
  };

  const brandOptions = useMemo(() => {
    return [
      ...new Set(allShopsData.map((shop) => shop.brand).filter(Boolean)),
    ].sort();
  }, [allShopsData]);

  const shopOptions = useMemo(() => {
    if (!selectedBrand) return [];

    return allShopsData
      .filter((shop) => shop.brand === selectedBrand)
      .sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? "")));
  }, [allShopsData, selectedBrand]);

  const visibleShopData = useMemo(() => {
    return (
      allShopsData
        .filter((shop) => {
          const matchesBrand = !selectedBrand || shop.brand === selectedBrand;

          const matchesShop =
            !selectedShop || String(shop.id) === String(selectedShop);

          // Shows either hidden shops or active shops depending on the selected view.
          const matchesHiddenView = showHiddenShops
            ? hiddenShopIds.includes(shop.id)
            : !hiddenShopIds.includes(shop.id);

          return matchesBrand && matchesShop && matchesHiddenView;
        })
        // Sort shops from newest to oldest.
        .sort((a, b) => {
          const dateA = a?.date_created
            ? new Date(a.date_created).getTime()
            : 0;

          const dateB = b?.date_created
            ? new Date(b.date_created).getTime()
            : 0;

          return dateB - dateA;
        })
    );
  }, [
    allShopsData,
    selectedBrand,
    selectedShop,
    hiddenShopIds,
    showHiddenShops,
  ]);

  const globalFilterFn = (row, _columnId, filterValue) => {
    const query = String(filterValue ?? "")
      .trim()
      .toLowerCase();

    if (!query) return true;

    const name = String(row.getValue("name") ?? "").toLowerCase();

    const brand = String(row.getValue("brand") ?? "").toLowerCase();

    const email = String(row.getValue("user_email") ?? "").toLowerCase();

    const country = String(row.getValue("country") ?? "").toLowerCase();

    const city = String(row.getValue("city") ?? "").toLowerCase();

    const projectId = String(row.getValue("projectId") ?? "").toLowerCase();

    const rawDate = row.getValue("date_created");

    const formattedDate = rawDate
      ? new Date(rawDate).toLocaleDateString("en-GB")
      : "";

    const dateText = `${rawDate ?? ""} ${formattedDate}`.toLowerCase();

    return (
      name.includes(query) ||
      brand.includes(query) ||
      email.includes(query) ||
      country.includes(query) ||
      city.includes(query) ||
      projectId.includes(query) ||
      dateText.includes(query)
    );
  };

  const columns = useMemo(
    () => [
      {
        header: "Shop Name",
        accessorKey: "name",
        cell: (info) => info.getValue() ?? "-",
      },
      {
        header: "Shop Brand",
        accessorKey: "brand",
        cell: (info) => info.getValue() ?? "-",
      },
      {
        header: "Email",
        accessorKey: "user_email",
        cell: (info) => info.getValue() ?? "-",
      },
      {
        header: "Creation Date",
        accessorKey: "date_created",
        cell: (info) => {
          const value = info.getValue();

          return value ? new Date(value).toLocaleDateString("en-GB") : "-";
        },
      },
      {
        header: "Country",
        accessorKey: "country",
        cell: (info) => info.getValue() ?? "-",
      },
      {
        header: "City",
        accessorKey: "city",
        cell: (info) => info.getValue() ?? "-",
      },
      {
        header: "Project ID",
        accessorFn: (row) => row.projectNumber || null,
        id: "projectId",

        cell: ({ row, getValue }) => {
          const shop = row.original;
          const value = getValue() ?? "-";
          const isEditing = editingShopId === shop.id;

          if (!isEditing) {
            return (
              <div className="flex min-w-0 items-center gap-4">
                <span
                  className="
                  max-w-[180px] truncate
                  lg:max-w-[400px]
                  lg:whitespace-normal
                  lg:break-words
                  lg:overflow-hidden
                  lg:text-clip
                  lg:line-clamp-2"
                  title={String(value)}
                >
                  {value}
                </span>

                {/* Project actions */}
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onOpenShopLogs?.(shop)}
                    className="flex items-center gap-1 rounded border border-stone-400 bg-stone-100 px-2 py-1 text-sm hover:bg-stone-200"
                    title="View Shop Logs"
                  >
                    <Logs size={14} />
                    <span className="hidden md:inline">Log Details</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => startEditProjectId(shop)}
                    className="flex items-center gap-1 rounded border border-stone-400 bg-stone-100 px-2 py-1 text-sm hover:bg-stone-200"
                    title="Edit Project ID"
                  >
                    <Pencil size={14} />
                    <span className="hidden md:inline">Edit Project ID</span>
                  </button>

                  {/* Hides an active shop or restores it when viewing hidden shops. */}
                  <button
                    type="button"
                    onClick={() => {
                      if (showHiddenShops) {
                        restoreShop(shop);
                      } else {
                        hideShop(shop);
                      }
                    }}
                    className="flex items-center gap-1 rounded border border-stone-400 bg-stone-100 px-2 py-1 text-sm hover:bg-stone-200"
                    title={showHiddenShops ? "Restore Shop" : "Hide Shop"}
                  >
                    {showHiddenShops ? <Eye size={14} /> : <EyeOff size={14} />}

                    {/* <span className="hidden md:inline">
                      {showHiddenShops ? "Restore" : "Hide"}
                    </span> */}
                  </button>

                  <button
                    type="button"
                    onClick={() => openShopInfo(shop)}
                    className="flex items-center gap-1 rounded border border-stone-400 bg-stone-100 px-2 py-1 text-sm hover:bg-stone-200"
                    title="View shop information"
                    aria-label={`View information for ${shop.name ?? "shop"}`}
                  >
                    <Info size={14} />
                    {/* <span className="hidden md:inline">Shop Info</span> */}
                  </button>

                  
                </div>
              </div>
            );
          }

          return (
            <div className="flex min-w-0 items-center gap-2">
              <input
                value={projectDraft}
                onChange={(event) => setProjectDraft(event.target.value)}
                className="min-w-0 w-40 max-w-full rounded border border-stone-200 px-2 py-1 text-sm"
                placeholder="Project ID"
                autoFocus
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    saveEditProject(shop);
                  }

                  if (event.key === "Escape") {
                    cancelEditProjectId();
                  }
                }}
              />

              <button
                type="button"
                onClick={() => saveEditProject(shop)}
                className="rounded p-1 hover:bg-stone-100"
                title="Save"
                aria-label="Save Project ID"
              >
                <Check size={16} />
              </button>

              <button
                type="button"
                onClick={cancelEditProjectId}
                className="rounded p-1 hover:bg-stone-100"
                title="Cancel"
                aria-label="Cancel Project ID editing"
              >
                <X size={16} />
              </button>
            </div>
          );
        },
      },
    ],
    [editingShopId, projectDraft, onOpenShopLogs, showHiddenShops],
  );

  const table = useReactTable({
    data: visibleShopData,
    columns,

    state: {
      pagination,
      globalFilter,
      sorting,
    },

    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,

    globalFilterFn,

    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isAllShopsDataPending) {
    return (
      <div className="flex h-64 items-center justify-center p-6">
        <OrbitProgress color="#444444" size="medium" />
      </div>
    );
  }

  if (allShopsDataError) {
    return (
      <div className="p-6 text-red-600">Error: {allShopsDataError.message}</div>
    );
  }

  const rowModel = table.getRowModel();

  const fullAddress = selectedShopInfo
    ? [
        selectedShopInfo.address1,
        selectedShopInfo.address2,
        selectedShopInfo.city,
        selectedShopInfo.zipcode,
        selectedShopInfo.country,
      ]
        .filter(Boolean)
        .join(", ") || "-"
    : "-";

  const creationDate = selectedShopInfo?.date_created
    ? new Date(selectedShopInfo.date_created).toLocaleString("en-GB")
    : "-";

  return (
    <div className="p-4 bg-stone-100 h-full overflow-hidden">
      <div className="flex h-[calc(100vh-180px)] flex-col rounded-lg border border-stone-200">
        <div className="flex flex-col gap-4 rounded-t-lg border-b border-stone-200 bg-stone-50 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
          {/* Page title */}

          <div className="w-full min-w-0 md:w-auto md:min-w-[360px]">
            <h1 className="flex items-center gap-2 text-lg font-bold text-stone-900">
              <Store size={20} />
              Shop List
            </h1>

            <p className="mt-1 text-sm text-stone-500">
              Manage all registered shops and search by brand, shop or customer.
            </p>
          </div>

          {/* Filters */}

          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:flex md:w-auto md:flex-wrap md:items-end">
            <div className="w-full md:w-[220px]">
              <label className="mb-1 block text-xs font-medium text-stone-600">
                Brand
              </label>

              <select
                value={selectedBrand}
                onChange={(event) => {
                  setSelectedBrand(event.target.value);
                  setSelectedShop("");

                  setPagination((previous) => ({
                    ...previous,
                    pageIndex: 0,
                  }));
                }}
                className="h-10 w-full rounded-lg border border-stone-300 bg-white px-3 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-300"
              >
                <option value="">All Brands</option>

                {brandOptions.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-[220px]">
              <label className="mb-1 block text-xs font-medium text-stone-600">
                Shop
              </label>

              <select
                value={selectedShop}
                onChange={(event) => {
                  setSelectedShop(event.target.value);

                  setPagination((previous) => ({
                    ...previous,
                    pageIndex: 0,
                  }));
                }}
                disabled={!selectedBrand}
                className="h-10 w-full rounded-lg border border-stone-300 bg-white px-3 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-300 disabled:bg-stone-100"
              >
                <option value="">
                  {selectedBrand ? "Select Shop" : "Select Brand First"}
                </option>

                {shopOptions.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            </div>

            <TableFilterPopover
              value={globalFilter}
              onChange={setGlobalFilter}
              onClear={() => setGlobalFilter("")}
              label="Search"
            />

            {/* Toggles between active shops and hidden shops. */}
            <button
              type="button"
              onClick={() => {
                setShowHiddenShops((previous) => !previous);
                setPagination((previous) => ({
                  ...previous,
                  pageIndex: 0,
                }));
              }}
              className="h-10 rounded-lg border border-stone-300 bg-white px-4 text-sm font-medium text-stone-700 hover:bg-stone-100"
              title={
                showHiddenShops ? "Show active shops" : "Show hidden shops"
              }
            >
              {showHiddenShops ? "Active Shops" : "Hidden Shops"}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-x-auto overflow-y-auto">
          <table className="min-w-full divide-y divide-stone-200">
            <thead className="bg-stone-100 sticky top-0 z-10 shadow-2xs">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`px-4 py-3 text-left text-xs font-semibold uppercase text-stone-600 ${
                        header.column.id === "country" ||
                        header.column.id === "date_created" ||
                        header.column.id === "user_email" 
                          ? "hidden md:table-cell"
                          : ""
                      }`}
                    >
                      {header.isPlaceholder ? null : (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="flex select-none items-center gap-2"
                          title="Sort"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}

                          {header.column.getIsSorted() === "asc" && (
                            <span className="text-stone-800">▲</span>
                          )}

                          {header.column.getIsSorted() === "desc" && (
                            <span className="text-stone-800">▼</span>
                          )}

                          {!header.column.getIsSorted() && (
                            <ArrowUpDown size={14} className="opacity-50" />
                          )}
                        </button>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody className="divide-y divide-stone-200 bg-white">
              {visibleShopData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-6 text-center text-sm text-stone-500"
                  >
                    No shops found
                  </td>
                </tr>
              ) : rowModel.rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-6 text-center text-sm text-stone-500"
                  >
                    No rows on this page
                  </td>
                </tr>
              ) : (
                rowModel.rows.map((row) => (
                  <tr key={row.id} className="hover:bg-stone-50">
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={`px-4 py-3 text-sm text-stone-800 ${
                          cell.column.id === "country" ||
                          cell.column.id === "date_created" ||
                          cell.column.id === "user_email"
                            ? "hidden md:table-cell"
                            : ""
                        }`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {visibleShopData.length > 0 && (
          <div className="relative flex items-center justify-between rounded-b-lg border-t border-stone-200 bg-stone-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-stone-600 md:absolute md:left-1/2 md:-translate-x-1/2">
              <button
                type="button"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="rounded-lg border border-stone-200 bg-white px-3 py-2 hover:bg-stone-100 disabled:opacity-40"
              >
                Prev
              </button>

              <div className="hidden md:block">
                Page{" "}
                <strong>{table.getState().pagination.pageIndex + 1}</strong> of{" "}
                <strong>{table.getPageCount()}</strong>
              </div>

              <button
                type="button"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="rounded-lg border border-stone-200 bg-white px-3 py-2 hover:bg-stone-100 disabled:opacity-40"
              >
                Next
              </button>
            </div>

            <div className="ml-auto flex items-center gap-3 text-sm text-stone-600">
              <div>
                Showing{" "}
                <strong>
                  {table.getState().pagination.pageIndex *
                    table.getState().pagination.pageSize +
                    1}
                </strong>{" "}
                –{" "}
                <strong>
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) *
                      table.getState().pagination.pageSize,
                    visibleShopData.length,
                  )}
                </strong>{" "}
                of <strong>{visibleShopData.length}</strong>
              </div>

              <div className="flex items-center gap-2">
                <label className="hidden md:block">Rows</label>

                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(event) =>
                    table.setPageSize(Number(event.target.value))
                  }
                  className="rounded-lg border border-stone-200 bg-white px-2 py-1 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {isShopInfoOpen && selectedShopInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Popup background */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeShopInfo}
          />

          {/* Popup content */}
          <div className="relative w-full max-w-xl overflow-hidden rounded-xl border border-stone-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-stone-900">
                  Shop Information
                </h2>

                <p className="mt-1 text-sm text-stone-500">
                  Complete information about the selected shop
                </p>
              </div>

              <button
                type="button"
                onClick={closeShopInfo}
                className="rounded-lg p-2 text-stone-500 hover:bg-stone-100 hover:text-stone-900"
                title="Close"
                aria-label="Close shop information"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4 p-6">
              <ShopInfoRow label="Email" value={selectedShopInfo.user_email} />

              <ShopInfoRow
                label="Project"
                value={selectedShopInfo.projectNumber}
              />

              <ShopInfoRow label="Brand" value={selectedShopInfo.brand} />

              <ShopInfoRow label="Shop" value={selectedShopInfo.name} />

              <ShopInfoRow label="Address" value={fullAddress} />

              <ShopInfoRow label="Country" value={selectedShopInfo.country} />

              <ShopInfoRow label="Creation Date" value={creationDate} />
            </div>

            <div className="flex justify-end border-t border-stone-200 px-6 py-4">
              <button
                type="button"
                onClick={closeShopInfo}
                className="rounded-lg bg-stone-100 border px-4 py-2 text-sm font-medium text-stone-800 hover:bg-stone-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Displays one label and value inside the shop information popup.
const ShopInfoRow = ({ label, value }) => {
  const displayValue =
    value !== null && value !== undefined && value !== "" ? String(value) : "-";

  return (
    <div className="grid grid-cols-1 gap-1 border-b border-stone-100 pb-3 sm:grid-cols-[140px_1fr]">
      <span className="text-sm font-semibold text-stone-600">{label}:</span>

      <span className="break-words text-sm text-stone-900">{displayValue}</span>
    </div>
  );
};

export default ShopListTable;
